import { FlowContextProvider, StepWrapper, useMutation, useReturnToPath } from '@betterment/js-runtime';
import { Route, Routes } from 'react-router-dom';

import { PinSetup } from './PinSetup';
import { VerifyCard } from './VerifyCard';

import { useCustomer } from '../Checking/components/CustomerProvider';
import LockedCardModal from '../Checking/components/LockedCardModal';
import { ActivateCardParams, PinResetParams } from '../Checking/data';
import { ModalContent, ModalOverlay } from '../Checking/modal-helpers';
import { isExpiringReissue, isLocked, isVerifiable, isVerified } from '../Checking/models/card';
import FullPageLoader from '../Shared/components/FullPageLoader';
import SuccessStep from '../Shared/components/SuccessStep';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

const CardSetupFlowController: React.FC = () => {
  const { customer } = useCustomer();

  const { card, cardPendingVerification } = customer;

  const isExpiringCardReplacement = isVerified(card) && isExpiringReissue(cardPendingVerification);
  const cardId = isExpiringCardReplacement ? cardPendingVerification!.id : card.id;
  const [postActivateCard] = useMutation<{}, ActivateCardParams>(urlBuilder.postActivateCard(cardId));
  const [postInitialPinSetup] = useMutation<{}, PinResetParams>(urlBuilder.postInitialPinSetup(cardId));

  const isCardLocked = isLocked(card);

  const returnToPath = useReturnToPath(urlBuilder.getAppChecking.absolute());
  if (!isVerifiable(card) && !isVerifiable(cardPendingVerification)) {
    locationAssign(returnToPath);
    return <FullPageLoader />;
  }

  const onActivateCard = async (params: ActivateCardParams) => {
    const response = await postActivateCard(params);
    return response;
  };

  return (
    <ModalOverlay>
      {isCardLocked && <LockedCardModal closeHandler={() => locationAssign(returnToPath)} />}
      <ModalContent aria-label="checking card setup">
        <FlowContextProvider
          returnToPath={urlBuilder.getAppChecking.absolute()}
          basePath="/checking/card_setup"
          routePath=""
        >
          <Routes>
            <Route
              data-feature="checking_card_management"
              path="/verify"
              element={
                <StepWrapper name="verify" next={isExpiringCardReplacement ? 'success' : 'pin'}>
                  {stepProps => (
                    <VerifyCard
                      {...stepProps}
                      onActivateCard={onActivateCard}
                      isExpiringCardReplacement={isExpiringCardReplacement}
                    />
                  )}
                </StepWrapper>
              }
            />
            <Route
              data-feature="checking_card_management"
              path="/pin"
              element={
                <StepWrapper name="pin">
                  {stepProps => <PinSetup {...stepProps} onInitialPinSetup={postInitialPinSetup} />}
                </StepWrapper>
              }
            />
            <Route
              data-feature="checking_card_management"
              path="/success"
              element={
                <StepWrapper name="success">
                  <SuccessStep redirectTo={returnToPath} message="Debit card activated" />
                </StepWrapper>
              }
            />
          </Routes>
        </FlowContextProvider>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CardSetupFlowController;
